import { useState, useEffect } from "react";
import { Switch, Route, useLocation } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { AdminSidebar } from "@/components/sidebars/AdminSidebar";
import { UserContactEditDialog } from "@/components/UserContactEditDialog";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { UserMenu } from "@/components/UserMenu";
import { Footer } from "@/components/Footer";
import AdminOverview from "@/pages/admin/AdminOverview";
import UnifiedCategories from "@/pages/admin/UnifiedCategories";
import CategoryBuilder from "@/pages/admin/CategoryBuilder";
import SystemSettings from "@/pages/admin/SystemSettings";
import AuditLog from "@/pages/admin/AuditLog";
import UserManagement from "@/pages/admin/UserManagement";
import TemplateAudit from "@/pages/admin/TemplateAudit";
import FormBuilderPage from "@/pages/admin/FormBuilder";
import GovernmentFormsAdmin from "@/pages/admin/GovernmentFormsAdmin";
import AdminFeedback from "@/pages/admin/AdminFeedback";
import NotFound from "@/pages/not-found";
import { useGuardedSignOut } from "@/components/LogoutSaveCheck";

function AdminRootRedirect() {
  const [, setLocation] = useLocation();
  useEffect(() => { setLocation("/dashboard"); }, []);
  return null;
}

function Router() {
  return (
    <Switch>
      <Route path="/dashboard" component={AdminOverview} />
      <Route path="/admin" component={AdminRootRedirect} />
      <Route path="/users" component={UserManagement} />
      <Route path="/categories" component={UnifiedCategories} />
      <Route path="/categories/:slug">{(params) => <CategoryBuilder slug={params.slug} />}</Route>
      <Route path="/template-audit" component={TemplateAudit} />
      <Route path="/form-builder" component={() => <FormBuilderPage />} />
      <Route path="/government-forms" component={GovernmentFormsAdmin} />
      <Route path="/settings" component={SystemSettings} />
      <Route path="/audit-log" component={AuditLog} />
      <Route path="/feedback" component={AdminFeedback} />
      <Route component={NotFound} />
    </Switch>
  );
}

export function AdministratorDashboard() {
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const { toast } = useToast();
  const { logout, user } = useAuth();

  // Auto-open password change dialog if user must change password
  useEffect(() => {
    if (user?.mustChangePassword) {
      setChangePasswordOpen(true);
    }
  }, [user?.mustChangePassword]);

  const updateProfileMutation = useMutation({
    mutationFn: async (data: { fullName: string; email: string; phoneNumber: string }) => {
      const res = await apiRequest("PATCH", "/api/user/profile", data);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({
        title: "Profile updated",
        description: "Your contact information has been updated successfully.",
      });
      setEditProfileOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating profile",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // s227 (LOGOUT-SAVE-CHECK-1): every sign-out goes through the one check (a no-op here: no Cat A values).
  const guardedSignOut = useGuardedSignOut();
  const handleLogout = () => guardedSignOut(async () => {
    try {
      await logout();
      toast({
        title: "Logged out",
        description: "You have been successfully logged out.",
      });
    } catch (error: any) {
      toast({
        title: "Logout failed",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  return (
    <div className="flex h-screen w-full">
      <AdminSidebar />
      <div className="flex flex-col flex-1 min-w-0">
        <header className="flex items-center justify-between p-4 shrink-0 nav-header">
          <div className="flex items-center gap-2">
            <SidebarTrigger data-testid="button-sidebar-toggle" />
            <h1 className="text-lg font-semibold" data-testid="text-admin-title">
              System Administration
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <UserMenu
              onLogout={handleLogout}
              onChangePassword={() => setChangePasswordOpen(true)}
              onEditProfile={() => setEditProfileOpen(true)}
              userName={user?.fullName}
            />
          </div>
        </header>
        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          <Router />
        </main>
        <Footer />
      </div>
      
      <UserContactEditDialog
        open={editProfileOpen}
        onOpenChange={setEditProfileOpen}
        onSubmit={(data) => updateProfileMutation.mutate(data)}
        user={user}
        isPending={updateProfileMutation.isPending}
      />
      <ChangePasswordDialog
        open={changePasswordOpen}
        onOpenChange={setChangePasswordOpen}
        mustChange={user?.mustChangePassword}
      />
    </div>
  );
}
